import { useState, useEffect, useCallback } from "react";
import {
  adminApi,
  type BillingPlansResult,
  type BillingSubscriptionsResult,
  type BillingPaymentsResult,
  explainAdminError,
} from "@/lib/superadmin";
import { PLAN_LABEL } from "@/lib/access";
import {
  PageHeader,
  Card,
  ErrorBanner,
  EmptyState,
  PageSkeleton,
  GhostButton,
} from "../components/ui";

function inr(value: number): string {
  return `₹${value.toLocaleString("en-IN")}`;
}

function fmtDate(v?: string): string {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const tabs = [
  { key: "plans", label: "Plans", icon: "ri-price-tag-3-line" },
  { key: "subscriptions", label: "Subscriptions", icon: "ri-repeat-line" },
  { key: "payments", label: "Payments", icon: "ri-exchange-line" },
];

export default function BillingPage() {
  const [activeTab, setActiveTab] = useState("plans");
  const [plans, setPlans] = useState<BillingPlansResult | null>(null);
  const [subs, setSubs] = useState<BillingSubscriptionsResult | null>(null);
  const [payments, setPayments] = useState<BillingPaymentsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [p, s, pay] = await Promise.all([
        adminApi.billing.plans(),
        adminApi.billing.subscriptions(),
        adminApi.billing.payments({ limit: 50 }).catch(() => ({ rows: [] as Record<string, unknown>[] })),
      ]);
      setPlans(p);
      setSubs(s);
      setPayments(pay);
    } catch (e) {
      setError(explainAdminError(e instanceof Error ? e.message : "Could not load billing data."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const paymentCols = payments?.rows.length ? Object.keys(payments.rows[0]) : [];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Subscription & Billing"
        subtitle="Plans, active subscriptions, and recorded payments"
        actions={
          <GhostButton onClick={() => void load()}>
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Refresh
          </GhostButton>
        }
      />

      <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-background-100 border border-background-200/70">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer ${
              activeTab === tab.key
                ? "bg-background-50 text-foreground-900 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          {activeTab === "plans" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans?.plans.length ? (
                plans.plans.map((p, i) => (
                  <Card
                    key={p.tier}
                    className={`relative overflow-hidden hover:shadow-md transition-shadow ${
                      i === 1 ? "ring-1 ring-primary-300" : ""
                    }`}
                  >
                    {i === 1 && (
                      <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 text-[10px] font-bold uppercase tracking-wide">
                        Popular
                      </span>
                    )}
                    <p className="text-xs uppercase tracking-wide text-foreground-400 font-semibold">
                      {p.label}
                    </p>
                    <p className="text-3xl font-heading font-bold text-foreground-900 mt-2">
                      {inr(p.price)}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">per {p.period}</p>
                  </Card>
                ))
              ) : (
                <Card className="md:col-span-3" padded={false}>
                  <EmptyState icon="ri-price-tag-3-line" title="No plans configured" />
                </Card>
              )}
            </div>
          )}

          {activeTab === "subscriptions" && (
            <Card padded={false} className="overflow-hidden">
              {subs?.rows.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-foreground-500 border-b border-background-200 bg-background-100/50">
                        <th className="px-4 py-3 font-medium">Restaurant ID</th>
                        <th className="px-4 py-3 font-medium">Plan</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Trial Ends</th>
                        <th className="px-4 py-3 font-medium">Period End</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {subs.rows.map((r) => (
                        <tr key={r.restaurantId} className="hover:bg-background-100/40">
                          <td className="px-4 py-3 font-mono text-xs text-foreground-600">{r.restaurantId}</td>
                          <td className="px-4 py-3 text-foreground-800">
                            {r.planTier ? PLAN_LABEL[r.planTier] ?? r.planTier : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700">
                              {r.status ?? "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-foreground-600">{fmtDate(r.trialEndsAt)}</td>
                          <td className="px-4 py-3 text-foreground-600">{fmtDate(r.currentPeriodEnd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="ri-repeat-line" title="No subscriptions found" />
              )}
            </Card>
          )}

          {activeTab === "payments" && (
            <Card padded={false} className="overflow-hidden">
              {payments?.rows.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-foreground-500 border-b border-background-200 bg-background-100/50">
                        {paymentCols.map((c) => (
                          <th key={c} className="px-4 py-3 font-medium whitespace-nowrap">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {payments.rows.map((row, i) => (
                        <tr key={i} className="hover:bg-background-100/40">
                          {paymentCols.map((c) => (
                            <td key={c} className="px-4 py-3 text-foreground-700 whitespace-nowrap">
                              {row[c] == null ? "—" : String(row[c])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  icon="ri-exchange-line"
                  title="No payment records yet"
                  hint="Payments will appear here as tenants pay their subscriptions."
                />
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
